import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Lock, User, LogOut } from "lucide-react";
import { LOGO_URL } from "@/data/constants";
import { useAuth } from "@/context/AuthContext";
import { useCedaActive } from "@/lib/ceda";
import { useBookLinks } from "@/lib/useBookLinks";

const baseLinks = [
  { to: "/", label: "Accueil" },
  { to: "/methode", label: "La Méthode" },
  { to: "/univers", label: "Les Univers" },
  { to: "/aventures", label: "L'Aventure" },
  { to: "/carnets", label: "Les Carnets" },
  { to: "/jeu", label: "Le Jeu" },
  { to: "/mon-espace", label: "Mon Espace" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const isAuthed = user && typeof user === "object";
  const cedaActive = useCedaActive();
  const links = cedaActive
    ? [...baseLinks.slice(0, 6), { to: "/ceda", label: "Les CEDA" }, baseLinks[6]]
    : baseLinks;

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F2E9]">
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-[#F6F2E9]/90 backdrop-blur-md border-b border-[#E8DCC4]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Tao des 4C" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#C9A24B]/40" />
            <span className="font-serif text-lg md:text-xl text-[#1F2A24] tracking-tight">
              Tao <span className="text-[#C9A24B]">des</span> 4C
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `font-sans text-sm tracking-wide transition-colors hover:text-[#385E3A] ${
                    isActive ? "text-[#385E3A] font-semibold" : "text-[#1F2A24]/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isAuthed ? (
              <button
                data-testid="nav-logout"
                onClick={logout}
                title={user.email}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#385E3A]/10 text-[#385E3A] px-3 py-1.5 font-sans text-sm hover:bg-[#385E3A]/20 transition-colors"
              >
                <User size={15} /> {user.name || "Mon compte"} <LogOut size={13} />
              </button>
            ) : (
              <Link
                to="/connexion"
                data-testid="nav-login"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#385E3A] text-white px-4 py-1.5 font-sans text-sm hover:bg-[#2c4a2e] transition-colors"
              >
                Se connecter
              </Link>
            )}
            <Link
              to="/admin"
              data-testid="nav-admin"
              title="Espace administrateur"
              aria-label="Administration"
              className="text-[#1F2A24]/35 hover:text-[#385E3A] transition-colors"
            >
              <Lock size={17} />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#1F2A24]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <nav className="md:hidden border-t border-[#E8DCC4] bg-[#F6F2E9] px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`py-2 font-sans text-sm ${
                  pathname === l.to ? "text-[#385E3A] font-semibold" : "text-[#1F2A24]/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
            {isAuthed ? (
              <button
                data-testid="nav-mobile-logout"
                onClick={() => { setOpen(false); logout(); }}
                className="py-2 text-left font-sans text-sm text-[#385E3A] font-semibold inline-flex items-center gap-2"
              >
                <LogOut size={15} /> Déconnexion ({user.name || user.email})
              </button>
            ) : (
              <Link
                to="/connexion"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-login"
                className="py-2 font-sans text-sm text-[#385E3A] font-semibold inline-flex items-center gap-2"
              >
                <User size={15} /> Se connecter
              </Link>
            )}
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-admin"
              className="py-2 font-sans text-sm text-[#1F2A24]/60 inline-flex items-center gap-2"
            >
              <Lock size={15} /> Administration
            </Link>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <Footer />
    </div>
  );
};

const Footer = () => {
  const bookLinks = useBookLinks();
  return (
  <footer data-testid="site-footer" className="bg-[#1F2A24] text-[#F6F2E9] mt-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 text-center">
      <div className="font-serif text-2xl mb-2">
        Tao <span className="text-[#C9A24B]">des</span> 4C
      </div>
      <p className="font-sans text-sm text-[#F6F2E9]/80">Corps • Cœur • Cerveau • Conscience</p>
      <div className="gold-divider max-w-xs mx-auto my-5" />
      <p className="font-serif italic text-lg text-[#F6F2E9]/90">Un chemin vers une vie plus pleine</p>
      <p className="font-sans text-xs tracking-[0.2em] text-[#C9A24B] mt-3">
        ÉQUILIBRER • RAYONNER • CRÉER • ÊTRE
      </p>
      <p className="font-sans text-[11px] text-[#F6F2E9]/50 mt-8 max-w-xl mx-auto leading-relaxed">
        Le Tao des 4C est un ouvrage de réflexion et d'exploration personnelle. Il ne constitue pas
        un avis médical ou thérapeutique, ni une promesse de résultat.
      </p>
      <div className="gold-divider max-w-xs mx-auto my-6" />
      <p className="font-sans text-xs text-[#F6F2E9]/60">
        Créé par <span className="text-[#C9A24B]">Denis Tran</span>
      </p>
      <a
        href="https://le-guerisseur.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="footer-guerisseur-link"
        className="inline-block font-sans text-xs text-[#F6F2E9]/45 mt-1 hover:text-[#C9A24B] transition-colors"
      >
        Découvrir l'univers de Denis Tran et ses autres activités →
      </a>
      <div className="gold-divider max-w-xs mx-auto my-6" />
      <p className="font-sans text-xs text-[#F6F2E9]/70">Le livre, disponible sur Amazon</p>
      <div className="flex items-center justify-center gap-5 mt-2">
        <a href={bookLinks.print} target="_blank" rel="noopener noreferrer" data-testid="footer-book-print"
           className="font-sans text-xs text-[#C9A24B] hover:text-white transition-colors">Livre broché →</a>
        <a href={bookLinks.ebook} target="_blank" rel="noopener noreferrer" data-testid="footer-book-ebook"
           className="font-sans text-xs text-[#C9A24B] hover:text-white transition-colors">Version Kindle →</a>
      </div>
    </div>
  </footer>
  );
};
